import React from "react";
import type { Favicon, PaletteOverrides } from "@riot-private/blades-schemas";
import {
  AnalyticsProvider,
  BetaMetadataProvider,
  BladesRenderer as SharedBladesRenderer,
  LoLChroma,
  PageBladesWrapper,
  SKINS_BY_THEME_ID,
  SkinProvider,
  type RenderBladeError,
} from "@riot-private/blades-ui";
import {
  Hreflang,
  PageProps,
  sanitizeString,
} from "@riot-private/blades-ui/blades-api-client";
import { useHubSdk } from "../../utils/hooks/use-hub-sdk";
import Head from "next/head";
import Script from "next/script";
import { DatadogRumScript } from "../DatadogRumScript";
import { DatadogRumErrors, type RumErrorEvent } from "../DatadogRumErrors";
import { WithSdkProvider } from "../WithSdkProvider";
import LegacyBladesRenderer from "../BladesRenderer";
import { ErrorBlade } from "../ErrorBlade";
import { RenderAvailabilityMiniBlade } from "../RenderAvailabilityMiniBlade";
import { WarningsBlade } from "../WarningsBlade";
import { getVwoScript } from "../../utils/vwo-script";
import { Markupbot } from "../markupbot/Markupbot";
import type { RiotbarRsoEnvironment } from "../../utils/riotbar-rso-environment";
import { createSiteBuilderBladeRegistry } from "../../utils/renderer/createSiteBuilderBladeRegistry";

/**
 * Extends PageProps with metadata that allows us to generate arbitrary meta tags for the page.
 * Temporarily defined here until we decide to make it CMS-controllable.
 */
export type PagePropsWithMeta = PageProps & {
  meta: Record<string, string>;
  debugData?: string[];
  riotbarEnvironment?: string;
  riotbarRsoEnvironment?: RiotbarRsoEnvironment;
  rumErrors?: RumErrorEvent[];
  /** Server-owned opt-out to retain the previous renderer for emergency rollback. */
  useLegacyBladeRenderer?: boolean;
  /** Server-derived eligibility; environment strings alone are not access control. */
  showBladeDiagnostics?: boolean;
  /**
   * Optional palette color overrides resolved from PALETTE_OVERRIDES_JSON in
   * fetchPageProps. Passed to blades-ui's SkinProvider, which merges them onto
   * the base skin's palette tokens at render time. Milestone 1 of the
   * CMS-driven Palette Overrides TDD.
   */
  paletteOverrides?: PaletteOverrides;
};

type FaviconLinks = Favicon[string] | undefined;

const bladeRegistry = createSiteBuilderBladeRegistry();
const renderBladeDiagnostic: RenderBladeError = (_error, context) => (
  <ErrorBlade {...context} />
);

export function BladesRendererPage({
  page,
  meta,
  debugData,
  environment,
  riotbarEnvironment,
  riotbarRsoEnvironment,
  rumErrors,
  useLegacyBladeRenderer = false,
  showBladeDiagnostics = false,
  datadogData,
  shouldRenderAvailability,
  pubHubSdkHost,
  betaMetadata,
  vwoAccountId,
  paletteOverrides,
}: PagePropsWithMeta) {
  const themeId = page.theme?.id;
  // SKINS_BY_THEME_ID is blades-ui's CMS-theme-id-to-skin map, replacing the SUPPORTED_CHROMAS
  // copy that used to live here. An unregistered id still falls back to LoLChroma.
  //
  // The hasOwn guard is load-bearing, not ceremony: SKINS_BY_THEME_ID is a plain object literal,
  // so a bare index returns a truthy prototype member for ids like "constructor" or "__proto__",
  // and `|| LoLChroma` would never fire. It also keeps the lookup immune to Object.prototype
  // pollution. Same guard as isMappedBladeType in RenderBlade.
  const skin =
    themeId && Object.hasOwn(SKINS_BY_THEME_ID, themeId)
      ? SKINS_BY_THEME_ID[themeId]
      : LoLChroma;

  const sanitizedGTMId =
    page?.gtmContainerId && sanitizeString(page.gtmContainerId);

  const sdk = useHubSdk(pubHubSdkHost, {
    env: environment,
    publishingLocale: page.locale,
  });
  const faviconIconLinks: FaviconLinks = page.favicon?.icon;
  const appleTouchIconLinks: FaviconLinks = page.favicon?.appleTouch;
  const shortcutIconLinks: FaviconLinks = page.favicon?.shortcut;

  return (
    <div>
      <Head>
        <title>{page.title}</title>
        {meta &&
          Object.entries(meta).map(([key, value]) => (
            <meta key={key} name={key} content={value} />
          ))}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        ></meta>
        <meta name="description" content={page.description} />
        {page.hreflangs?.map((herflang: Hreflang) => (
          <link
            key={herflang.locale}
            rel="alternate"
            href={herflang.url}
            hrefLang={herflang.locale}
          />
        ))}
        {page.url ? <link rel="canonical" href={page.url} /> : null}
        <meta property="og:url" content={page.url} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta
          property="og:image"
          content={`${page.metaImage?.url}?w=1200&h=630&fm=webp&fit=crop&crop=center`}
        />
        <meta property="og:locale" content={page.locale} />
        <Markupbot page={page} />
        {faviconIconLinks?.map((icon, index) => (
          <link
            rel="icon"
            href={icon.icon}
            sizes={icon.size}
            key={`icon-${index}`}
          />
        ))}
        {appleTouchIconLinks?.map((appleTouch, index) => (
          <link
            rel="apple-touch-icon"
            href={appleTouch.icon}
            sizes={appleTouch.size}
            key={`apple-touch-icon-${index}`}
          />
        ))}
        {shortcutIconLinks?.map((shortcut, index) => (
          <link
            rel="shortcut icon"
            href={shortcut.icon}
            sizes={shortcut.size}
            key={`shortcut-icon-${index}`}
          />
        ))}
        {!!vwoAccountId && (
          <link
            rel="preconnect"
            href="https://dev.visualwebsiteoptimizer.com"
          />
        )}
        {/* DO NOT ADD ANY SCRIPTS ABOVE OSANO OR GOOGLE CONSENT. THEY MUST ALWAYS LOAD FIRST. */}
        {/* Google Consent Script */}
        {/* Note: Using script instead of Script because there are existing bugs regarding ordering of scripts in next.js */}
        <script
          id="google-consent-script"
          dangerouslySetInnerHTML={{
            __html: `try {
              window.dataLayer = window.dataLayer ||[];
              function gtag(){dataLayer.push(arguments);}
              gtag('consent','default',{
                'ad_storage':'denied',
                'analytics_storage':'denied',
                'ad_user_data':'denied',
                'ad_personalization':'denied',
                'wait_for_update': 500
              });
              gtag("set", "ads_data_redaction", true);
            } catch(e) {}`,
          }}
        />
        {/* OSANO */}
        {page.osanoPolicyId && (
          // Using script instead of Script because there are existing bugs regarding ordering of scripts in next.js
          // eslint-disable-next-line @next/next/no-sync-scripts
          <script
            id="osano-script"
            src={`https://cmp.osano.com/16BZ95S4qp9Kl2gUA/${page.osanoPolicyId}/osano.js`}
          />
        )}
        {/* Google Tag Manager */}
        {/* Using script instead of Script because there are existing bugsregarding ordering of scripts in next.js */}
        {/* TODO: Consider using @next/third-parties https://nextjs.org/docs/messages/next-script-for-ga */}
        {/* eslint-disable-next-line @next/next/no-sync-scripts, @next/next/next-script-for-ga */}
        <script
          id="google-tag-manager"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${sanitizedGTMId}');`,
          }}
        />
      </Head>

      {/* VWO script */}
      {!!vwoAccountId && (
        // TODO: This is not entirely correct, beforeInteactive scripts should be added in _document.
        // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
        <Script
          id="vwoCode"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{ __html: getVwoScript(vwoAccountId) }}
        />
      )}

      <DatadogRumScript datadogData={datadogData} environment={environment} />
      <DatadogRumErrors rumErrors={rumErrors} />

      <main>
        {sanitizedGTMId && (
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe
              src='https://www.googletagmanager.com/ns.html?id=${sanitizedGTMId}'
              height="0"
              width="0"
              style="display: none; visibility: hidden;"></iframe>`,
            }}
          />
        )}

        <WithSdkProvider sdk={sdk}>
          <SkinProvider
            registry={bladeRegistry}
            skin={skin}
            paletteOverrides={paletteOverrides}
            locale={page.locale}
            dir={page.locale === "ar-ae" ? "rtl" : "ltr"}
            environment={environment}
            riotbarEnvironment={riotbarEnvironment || environment}
            riotbarRsoEnvironment={riotbarRsoEnvironment}
          >
            <AnalyticsProvider
              page={{
                id: page.translationId,
                url: page.url,
                locale: page.locale || "en-us",
                // TODO: version should come from the API or env variable
                version: "2",
              }}
            >
              <BetaMetadataProvider {...betaMetadata}>
                <PageBladesWrapper>
                  {useLegacyBladeRenderer ? (
                    <LegacyBladesRenderer
                      blades={page.blades}
                      locale={page.locale || "en-us"}
                      environment={environment}
                      showBladeDiagnostics={showBladeDiagnostics}
                    />
                  ) : (
                    <>
                      <header>
                        <div id="riotbar-header" />
                      </header>
                      <SharedBladesRenderer
                        blades={page.blades}
                        {...(showBladeDiagnostics
                          ? { renderError: renderBladeDiagnostic }
                          : {})}
                      />
                      <footer>
                        <div id="riotbar-footer" />
                      </footer>
                    </>
                  )}
                  {shouldRenderAvailability && (
                    <RenderAvailabilityMiniBlade locale={page.locale} />
                  )}
                  {page.warnings?.length || debugData ? (
                    <WarningsBlade
                      environment={environment}
                      warnings={page.warnings}
                    >
                      {debugData}
                    </WarningsBlade>
                  ) : null}
                </PageBladesWrapper>
              </BetaMetadataProvider>
            </AnalyticsProvider>
          </SkinProvider>
        </WithSdkProvider>
      </main>
    </div>
  );
}
